import React, { useState } from 'react';
import Linkify from 'react-linkify';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Pin, Reply, Trash2, Crown, Shield, Star, DollarSign, X } from 'lucide-react';
import URLViewerModal from '../URLViewerModal';
import { getImageUrl } from '../../utils/imageUtils';
import { useSecureUrl } from '../../hooks/useSecureUrl'; // Import useSecureUrl


/**
 * Renders role badge for user
 */
export const getRoleBadge = (role) => {
  switch (role) {
    case 'super_admin':
      return <span className="ml-1 text-red-500" title="Admin"><Crown className="w-4 h-4 inline" /></span>;
    case 'staff':
      return <span className="ml-1 text-purple-500" title="Staff"><Shield className="w-4 h-4 inline" /></span>;
    case 'premium':
      return <span className="ml-1 text-amber-500" title="Premium"><Star className="w-4 h-4 inline" /></span>;
    case 'vip':
      return <span className="ml-1 text-purple-600" title="VIP"><DollarSign className="w-4 h-4 inline" /></span>;
    default:
      return null;
  }
};

/**
 * Renders user badges
 */
export const renderUserBadges = (badgeDetails) => {
  if (!badgeDetails || !Array.isArray(badgeDetails) || badgeDetails.length === 0) return null;
  
  return (
    <div className="flex items-center gap-0.5 ml-1">
      {badgeDetails.slice(0, 3).map((badge, idx) => (
        <span 
          key={badge.id || idx}
          title={badge.name}
          className="text-sm"
          style={{ fontSize: '14px' }}
        >
          {badge.icon}
        </span>
      ))}
      {badgeDetails.length > 3 && (
        <span className="text-xs text-gray-400">+{badgeDetails.length - 3}</span>
      )}
    </div>
  );
};



/**
 * Renders message content with mention highlighting and secure clickable links
 */
export const renderMessageContent = (content, currentUserId, linkDecorator) => {
  if (!content) return null;
  
  // Check if content is a media URL (image/video/gif)
  const isMediaUrl = content.match(/\.(jpg|jpeg|png|gif|mp4|webm|mov)$/i) || 
    content.includes('giphy.com') || 
    content.includes('klipy.com') ||
    content.includes('static.klipy.com') ||
    content.includes('/api/media/');
  
  // If it's a media URL, don't linkify it (it will be handled separately)
  if (isMediaUrl && content.startsWith('http')) {
    return content;
  }
  
  // First, handle @mentions
  const parts = content.split(/(@\w+)/g);
  
  return (
    <Linkify componentDecorator={linkDecorator}>
      {parts.map((part, index) => {
        if (part.startsWith('@')) {
          return (
            <span 
              key={index} 
              className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-1 rounded font-semibold"
            >
              {part}
            </span>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </Linkify>
  );
};

/**
 * ChatMessage - Renders a single chat message with secure URL handling
 */
const ChatMessage = ({
  msg,
  user,
  roomTheme = {},
  globalTheme = {},
  canModerate = false,
  onAvatarClick,
  onPinMessage,
  onUnpinMessage,
  onReply,
  onDelete,
  onMediaClick,
  API
}) => {
  const { urlModalOpen, setUrlModalOpen, viewUrl, handleLinkClick, linkDecorator } = useSecureUrl();

  const isOwnMessage = msg.sender_id === user.id;
  const fontFamily = ((roomTheme.font_family || globalTheme.font_family || '') + '').replace(/\+/g, ' ') || 'inherit';
  
  // Check if content is media
  const isMediaContent = msg.content?.startsWith('http') && 
    (msg.content.includes('/media/') || msg.content.match(/\.(jpg|jpeg|png|gif|mp4|webm|mov|avi|ogg|quicktime)$/i));
  const isVideo = msg.content?.match(/\.(mp4|webm|mov|avi|ogg|quicktime)$/i);
  const isImage = msg.content?.match(/\.(jpg|jpeg|png|gif)$/i) && !isVideo || 
    msg.content?.includes('giphy.com') || 
    msg.content?.includes('klipy.com') || 
    msg.content?.includes('static.klipy.com');

  // System message
  if (msg.type === 'system') {
    return (
      <div className="text-center text-sm text-gray-500 py-2">{msg.content}</div>
    );
  }

  return (
    <>
      {/* URL Viewer Modal */}
      <URLViewerModal
        isOpen={urlModalOpen}
        onClose={() => setUrlModalOpen(false)}
        url={viewUrl}
      />

      <div className={`flex gap-3 ${isOwnMessage ? 'flex-row-reverse' : ''} group`}>
        {/* Avatar */}
        <Avatar 
          className={`w-8 h-8 ${!isOwnMessage ? 'cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all' : ''}`}
          onClick={() => !isOwnMessage && onAvatarClick?.(msg.sender?.username, msg.sender_id)}
          title={!isOwnMessage ? `Click to mention @${msg.sender?.username}` : ''}
        >
          <AvatarImage src={getImageUrl(msg.sender?.avatar_url)} />
          <AvatarFallback className="bg-blue-600 text-white text-xs">
            {msg.sender?.username?.substring(0, 2).toUpperCase() || '??'}
          </AvatarFallback>
        </Avatar>

        {/* Message Content */}
        <div className={`flex flex-col ${isOwnMessage ? 'items-end' : ''}`}>
          {/* Header: Username, Role Badge, Time */}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300" style={{ fontFamily }}>
              {msg.sender?.display_name || msg.sender?.username}
            </span>
            {msg.sender && getRoleBadge(msg.sender.role)}
            {msg.sender?.badge_details && renderUserBadges(msg.sender.badge_details)}
            <span className="text-xs text-gray-400" style={{ fontFamily }}>
              {new Date(msg.created_at).toLocaleTimeString()}
            </span>
            
            {/* Pin button for moderators */}
            {canModerate && !msg.pinned && (
              <button
                onClick={() => onPinMessage?.(msg.id)}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
                title="Pin message"
              >
                <Pin className="w-3 h-3 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>

          {/* Reply Context */}
          {msg.parent_message && (
            <div className={`text-xs mb-1 p-2 rounded border-l-2 ${
              isOwnMessage
                ? 'border-blue-300 bg-blue-50 dark:bg-blue-900/30 text-gray-700 dark:text-gray-300'
                : 'border-gray-300 bg-gray-50 dark:bg-gray-700'
            }`}>
              <div className="font-semibold">
                Reply to {msg.parent_message.sender?.username}:
              </div>
              <div className="truncate opacity-75">
                {msg.parent_message.content?.substring(0, 50)}
                {msg.parent_message.content?.length > 50 ? '...' : ''}
              </div>
            </div>
          )}

          {/* Message Bubble */}
          <div
            className={`px-4 py-2 rounded-2xl max-w-md ${!isOwnMessage ? 'border border-gray-200 dark:border-gray-600' : ''}`}
            style={{
              fontFamily,
              backgroundColor: isOwnMessage 
                ? (globalTheme.chat_bubble_own || '#2563EB')
                : (globalTheme.chat_bubble_other || '#FFFFFF'),
              color: isOwnMessage 
                ? '#ffffff' 
                : (globalTheme.font_color || roomTheme.font_color || '#000000')
            }}
          >
            {/* Media Content */}
            {isMediaContent ? (
              isImage ? (
                <div 
                  className="cursor-pointer group/media relative"
                  onClick={() => onMediaClick?.({ url: msg.content, type: 'image' })}
                  title="Click to view full size"
                >
                  <img 
                    src={getImageUrl(msg.content)} 
                    alt="media" 
                    className="rounded max-w-[120px] md:max-w-[180px] h-auto hover:opacity-90 transition-opacity"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover/media:bg-black/20 transition-all rounded flex items-center justify-center">
                    <span className="text-white opacity-0 group-hover/media:opacity-100 text-xs bg-black/50 px-2 py-1 rounded">
                      Click to enlarge
                    </span>
                  </div>
                </div>
              ) : isVideo ? (
                <div 
                  className="cursor-pointer"
                  onClick={() => onMediaClick?.({ url: msg.content, type: 'video' })}
                >
                  <video 
                    src={getImageUrl(msg.content)} 
                    className="rounded max-w-[120px] md:max-w-[180px] h-auto"
                    muted
                    playsInline
                  />
                  <span className="text-xs opacity-75 mt-1 block">Click to play</span>
                </div>
              ) : (
                <span>{renderMessageContent(msg.content, user.id, linkDecorator)}</span>
              )
            ) : (
              <span>{renderMessageContent(msg.content, user.id, linkDecorator)}</span>
            )}
          </div>

          {/* Action Buttons */}
          <div className={`flex gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity ${isOwnMessage ? 'flex-row-reverse' : ''}`}>
            <button
              onClick={() => onReply?.(msg)}
              className="text-xs text-gray-400 hover:text-blue-500 px-2 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Reply"
            >
              <Reply className="w-3 h-3 inline mr-1" />
              Reply
            </button>
            {(isOwnMessage || canModerate) && (
              <button
                onClick={() => onDelete?.(msg.id)}
                className="text-xs text-gray-400 hover:text-red-500 px-2 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Delete"
              >
                <Trash2 className="w-3 h-3 inline mr-1" />
                Delete
              </button>
            )}
            {canModerate && msg.pinned && (
              <button
                onClick={() => onUnpinMessage?.(msg.id)}
                className="text-xs text-gray-400 hover:text-orange-500 px-2 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Unpin"
              >
                <X className="w-3 h-3 inline mr-1" />
                Unpin
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatMessage;
